import { Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ApiError, handleError } from '../../../api/base';
import PlanogramService from '../../../api/planogram/services';
import { useAuth } from '../../../hooks/useAuth';
import { useUnityBridge } from '../../../hooks/useUnityBridge';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getClient } from '../../../store/features/clients/clientsSlice';
import { UserPermissions } from '../../../util';
import NoPermission from '../../elements/NoPermission';
import UnityPlayer from './UnityPlayer';

interface ParamsState {
  id: string;
}

const PlanogramDetailsView = () => {
  const { id: projectId } = useParams<ParamsState>();
  const history = useHistory();
  const [isDirty, setIsDirty] = useState(false);
  const [client2dLibraryPath, setClient2dLibraryPath] = useState('');
  const dispatch = useAppDispatch();
  const { client } = useAppSelector((state) => state.clients);
  const { hasPermission } = useAuth();
  const requestedPlanogramIdRef = useRef<string | null>(null);

  const {
    exportImage,
    exportCSV,
    exportZip,
    exportPDF,
    exportPSA,
    sendAuthToken,
    loadProject,
  } = useUnityBridge({
    projectId,
    callbacks: {
      onExportImage: (
        imageBytes: Uint8Array,
        fileName: string,
        extension: string
      ) => {
        console.log(
          'Message received from Unity: Exporting image ',
          imageBytes,
          fileName,
          extension
        );
        exportImage(imageBytes, fileName, extension);
      },
      onExportCSV: (csvString: string, fileName: string, extension: string) => {
        console.log(
          'Message received from Unity: Exporting CSV ',
          csvString,
          fileName,
          extension
        );
        exportCSV(csvString, fileName, extension);
      },
      onExportZip: (
        zipFile: Uint8Array,
        fileName: string,
        extension: string
      ) => {
        console.log(
          'Message received from Unity: Exporting ZIP ',
          zipFile,
          fileName,
          extension
        );
        exportZip(zipFile, fileName, extension);
      },
      onExportPDF: (pdfJSON: string, fileName: string, extension: string) => {
        console.log(
          'Message received from Unity: Exporting PDF ',
          pdfJSON,
          fileName,
          extension
        );
        exportPDF(pdfJSON, fileName, extension);
      },
      onExportPSA: (
        psaBytes: Uint8Array | number[],
        fileName: string,
        extension: string
      ) => {
        console.log(
          'Message received from Unity: Exporting PSA file',
          fileName,
          extension
        );
        exportPSA(psaBytes, fileName, extension);
      },
      onAuthReady: () => {
        console.log('Message received from Unity: Auth ready');
        sendAuthToken();
      },
      onProjectReady: () => {
        console.log('Message received from Unity: Project ready');
        if (projectId) {
          console.log('Loading project:', projectId);
          console.log('Client 2D library path:', client2dLibraryPath);
          loadProject(projectId, client2dLibraryPath);
        }
      },
      onSetDirty: (dirty: boolean) => {
        console.log('Project dirty state changed:', dirty);
        setIsDirty(dirty);
      },
      onReturnToProjects: () => {
        console.log('Message received from Unity: Return to projects');
        history.push('/planogram');
      },
    },
  });

  // Send the authentication token to Unity
  useEffect(() => {
    console.log('Sending authentication token to Unity');
    sendAuthToken();
  }, [sendAuthToken]);

  // Block the user from leaving the page if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Block the user from leaving the page if there are unsaved changes
  useEffect(() => {
    const unblock = history.block((tx: any): false | void => {
      if (!isDirty || tx.pathname === `/planogram/${projectId}`) {
        return;
      }

      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to leave?',
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => {
          setIsDirty(false);
          setTimeout(() => {
            unblock();
            history.push(tx.pathname);
          }, 0);
        },
        onCancel: () => {},
      });

      return false;
    });

    return () => {
      unblock();
    };
  }, [history, isDirty, projectId]);

  // Get the current client from localStorage
  useEffect(() => {
    try {
      const me = localStorage.getItem('me');
      if (me) {
        const userData = JSON.parse(me);
        if (userData.client_id) {
          dispatch(getClient({ id: userData.client_id }));
        }
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
  }, [dispatch]);

  // Set the client 2D library path
  useEffect(() => {
    if (client) {
      setClient2dLibraryPath(client['library_path_2d'] || '');
    }
  }, [client]);

  // Validate access to the current planogram project (handles client switch)
  useEffect(() => {
    const validateAccess = async () => {
      if (!projectId) return;

      // Avoid duplicate requests under StrictMode and re-renders
      if (requestedPlanogramIdRef.current === projectId) return;

      requestedPlanogramIdRef.current = projectId;
      try {
        await PlanogramService().getPlanogram(projectId);
      } catch (error) {
        requestedPlanogramIdRef.current = null; // allow retry on failure
        handleError(error as ApiError);
      }
    };

    validateAccess();
  }, [projectId]);

  if (!hasPermission(UserPermissions.PlanogramProjectsView)) {
    return <NoPermission />;
  }

  return <UnityPlayer />;
};

export default PlanogramDetailsView;
